import { BorderRadius, Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { MapMarker, MapMarkerStyleType } from 'dashboard/components/Map/MapMarker';
import Tooltip from 'dashboard/components/Tooltip';
import useToggle from 'lib/hooks/useToggle';

interface MarkerProps extends MapMarker {
  /**
   * Reference to the actual Google Map canvas
   * Only needed if we're accessing the internal map API
   */
  map?: google.maps.Map;
  /**
   * Not currently used.
   * Indicates if the marker has been activated externally; e.g. if the
   * corresponding address is interacted elsewhere in the app.
   */
  isActive: boolean;
  /**
   * If needed, this component can also take some kind of ID / desc /
   * other string like address for better tooltip rendering.
   */
  renderTooltip?: (marker: MapMarker) => ReactNode;
  /**
   * Shows a translucent ellipse under the marker to indicate the coordinate
   * is not an exact location.
   */
  isApproximate?: boolean;
  /**
   * Used to increase size of marker on hover, based on how many items
   * share the same location.
   */
  overlapCount?: number;
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
type MarkerPalette = Record<MarkerProps['styleType'], string>;

const OVERLAY_BORDER_COLORS: MarkerPalette = {
  default: Color.CorePurple900,
  secondary: Color.CoreSlate1000,
  'count-based': Color.CorePurple900,
  'circle-only': Color.CoreSlate1000,
};

const OVERLAY_FILL_COLORS: MarkerPalette = {
  default: Color.CorePurple900,
  secondary: Color.CoreSlate1000,
  'count-based': Color.CorePurple900,
  'circle-only': Color.CoreSlate1000,
};

const SELECTED_CIRCLE_OPACITY = 0.4;
const DEFAULT_CIRCLE_OPACITY = 0.2;

const SELECTED_CIRCLE_BORDER_OPACITY = 1.0;
const DEFAULT_CIRCLE_BORDER_OPACITY = 0.4;

const DEFAULT_MARKER_COLOR = Color.CoreRed800;
const MARKER_HEIGHT = 40;
const MARKER_WIDTH = 32;
const MARKER_ELLIPSE_RADIUS = 32;
const MARKER_INDICATOR_RADIUS = 6;
/**
 * Shifts marker down to give room to grow on active.
 */
const MARKER_TRANSFORM_OFFSET = 10;

const MarkerTooltip = styled(Tooltip)`
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacing[6]};
  ${Typography.TextMedium}

  padding: ${Spacing[2]};
  border-radius: ${BorderRadius.Md};
`;

const Marker = ({
  lat,
  lng,
  radiusMeters,
  styleType = MapMarkerStyleType.Default,
  overlapCount = 1,
  primaryTooltipLabel,
  secondaryTooltipLabel,
  focusedMarkerColor,
  defaultMarkerColor,
  hasDot,
  map,
  isActive,
  renderTooltip,
  isApproximate,
}: MarkerProps) => {
  const [isHovered, { enable: turnOnHover, disable: turnOffHover }] = useToggle(false);
  const hoveredOrActive = isHovered || isActive;

  const circleOverlayRef = useRef(null);

  const setCircleOverlay = (circle: google.maps.Circle) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    circleOverlayRef.current = circle;
  };

  const handleMouseOver = useCallback(() => {
    turnOnHover();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    circleOverlayRef.current?.setOptions({
      fillOpacity: SELECTED_CIRCLE_OPACITY,
      strokeOpacity: SELECTED_CIRCLE_BORDER_OPACITY,
    });
  }, []);

  const handleMouseOut = useCallback(() => {
    turnOffHover();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    circleOverlayRef.current?.setOptions({
      fillOpacity: DEFAULT_CIRCLE_OPACITY,
      strokeOpacity: DEFAULT_CIRCLE_BORDER_OPACITY,
    });
  }, []);

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    circleOverlayRef.current?.setOptions({
      strokeColor: OVERLAY_BORDER_COLORS[styleType],
      fillColor: OVERLAY_FILL_COLORS[styleType],
      strokeOpacity: isActive ? SELECTED_CIRCLE_BORDER_OPACITY : DEFAULT_CIRCLE_BORDER_OPACITY,
      fillOpacity: isActive ? SELECTED_CIRCLE_OPACITY : DEFAULT_CIRCLE_OPACITY,
    });
  }, [isActive, styleType]);

  useEffect(() => {
    if (map && radiusMeters) {
      setCircleOverlay(
        new google.maps.Circle({
          strokeWeight: 2,
          strokeColor: OVERLAY_BORDER_COLORS[styleType],
          fillColor: OVERLAY_FILL_COLORS[styleType],
          strokeOpacity: DEFAULT_CIRCLE_BORDER_OPACITY,
          fillOpacity: DEFAULT_CIRCLE_OPACITY,
          map: map,
          center: { lat, lng },
          radius: radiusMeters,
        }),
      );
    }

    return () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      circleOverlayRef.current?.setMap(null);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setCircleOverlay(null);
    };
  }, [map, lat, lng, radiusMeters, styleType]);

  // TODO: Tooltip content is temporary; can be changed to whatever is appropriate
  return (
    <MarkerTooltip
      placement='top'
      tooltipShown={isActive || undefined}
      tooltip={
        renderTooltip != null
          ? renderTooltip({
              lat: lat,
              lng: lng,
              radiusMeters: radiusMeters,
              primaryTooltipLabel: primaryTooltipLabel,
              secondaryTooltipLabel: secondaryTooltipLabel,
            })
          : null
      }
      hideArrow={true}
      disabled={renderTooltip == null}
    >
      {(() => {
        switch (styleType) {
          case MapMarkerStyleType.CircleOnly: {
            return (
              <svg
                style={{
                  position: 'absolute',
                  top: `-${MARKER_WIDTH / 2}px`,
                  left: `-${MARKER_WIDTH / 2}px`,
                }}
                xmlns='http://www.w3.org/2000/svg'
                width={MARKER_WIDTH}
                height={MARKER_WIDTH}
                fill='none'
                viewBox={`0 0 ${MARKER_WIDTH} ${MARKER_WIDTH}`}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}
              >
                <circle
                  style={{
                    borderStyle: 'solid',
                    borderColor: Color.PrimaryWhite,
                    borderWidth: '2px',
                    opacity: 1,
                  }}
                  cx={MARKER_WIDTH / 2}
                  cy={MARKER_WIDTH / 2}
                  r={MARKER_INDICATOR_RADIUS}
                  fill={OVERLAY_FILL_COLORS[styleType]}
                  stroke={Color.PrimaryWhite}
                  strokeWidth={2}
                />
              </svg>
            );
          }
          case MapMarkerStyleType.CountBased: {
            let diameter = 16 + (overlapCount > 1 ? 16 + (overlapCount - 2) * 6 : 0);
            diameter = diameter > 64 ? 64 : diameter;
            const radius = diameter / 2;
            const borderStroke = overlapCount > 1 ? 2 : 0;

            return (
              <svg
                style={{
                  position: 'absolute',
                  top: -radius,
                  left: -radius,
                  filter: 'drop-shadow(2px 2px 2px rgba(2, 0, 0, 0.1)',
                }}
                xmlns='http://www.w3.org/2000/svg'
                width={diameter + borderStroke}
                height={diameter + borderStroke}
                fill='none'
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}
              >
                <circle
                  style={{
                    borderStyle: 'solid',
                    borderColor: Color.PrimaryWhite,
                    borderWidth: borderStroke,
                  }}
                  cx='50%'
                  cy='50%'
                  r={radius}
                  fill={Color.CoreRed900}
                  stroke={Color.PrimaryWhite}
                  strokeWidth={borderStroke}
                />
                {borderStroke && (
                  <text
                    x={radius}
                    y={radius + 6}
                    textAnchor='middle'
                    fill={Color.PrimaryWhite}
                    fontSize={Typography.TextMedium.fontSize}
                    fontFamily={Typography.TextMedium.fontFamily}
                  >
                    {overlapCount}
                  </text>
                )}
              </svg>
            );
          }
          default: {
            const radius = MARKER_WIDTH / 2;
            const hasOverlap = overlapCount && overlapCount > 1;
            const primaryColor =
              (hoveredOrActive ? focusedMarkerColor : defaultMarkerColor) || DEFAULT_MARKER_COLOR;
            return (
              <svg
                style={{
                  position: 'absolute',
                  top: `-${MARKER_HEIGHT - MARKER_ELLIPSE_RADIUS / 2 + MARKER_TRANSFORM_OFFSET}px`,
                  left: `-${MARKER_WIDTH / 2}px`,
                }}
                xmlns='http://www.w3.org/2000/svg'
                width={MARKER_WIDTH}
                height={MARKER_HEIGHT + MARKER_TRANSFORM_OFFSET}
                fill='none'
                viewBox={`0 -${MARKER_TRANSFORM_OFFSET} ${MARKER_WIDTH} ${
                  MARKER_HEIGHT + MARKER_TRANSFORM_OFFSET
                }`}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}
              >
                <g
                  style={{
                    transform: hoveredOrActive ? 'scale(1.25)' : undefined,
                    transformOrigin: `center ${MARKER_HEIGHT - MARKER_ELLIPSE_RADIUS / 2}px`,
                    transition: 'transform .2s',
                  }}
                  stroke={primaryColor}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='6.5'
                  fillRule='nonzero'
                >
                  <path
                    fillOpacity={1}
                    fill={hasOverlap ? primaryColor : undefined}
                    d='M25 10C25 17 16 23 16 23C16 23 7 17 7 10C7 7.61305 7.94821 5.32387 9.63604 3.63604C11.3239 1.94821 13.6131 1 16 1C18.3869 1 20.6761 1.94821 22.364 3.63604C24.0518 5.32387 25 7.61305 25 10Z'
                  />
                </g>
                {hasDot && (
                  <circle
                    style={{
                      borderStyle: 'solid',
                      borderColor: Color.PrimaryWhite,
                      borderWidth: '2px',
                      opacity: hoveredOrActive ? 0 : 1,
                      transitionDuration: '0.1s',
                    }}
                    cx={MARKER_WIDTH - MARKER_INDICATOR_RADIUS - 1}
                    cy={2}
                    r={MARKER_INDICATOR_RADIUS}
                    fill={Color.CoreRed800}
                    stroke={Color.PrimaryWhite}
                    strokeWidth={2}
                  />
                )}
                {isApproximate && (
                  <circle
                    style={{
                      opacity: hoveredOrActive ? 0.3 : 0.2,
                      transition: 'opacity .2s',
                    }}
                    cx={MARKER_ELLIPSE_RADIUS / 2}
                    cy={MARKER_HEIGHT - MARKER_ELLIPSE_RADIUS / 2}
                    r={MARKER_ELLIPSE_RADIUS / 2}
                    fill={Color.PrimaryWhite}
                  />
                )}
                {hasOverlap && (
                  <text
                    x={radius}
                    y={radius - 2}
                    textAnchor='middle'
                    fill={Color.PrimaryWhite}
                    fontSize={Typography.TextMedium.fontSize}
                    fontFamily={Typography.TextMedium.fontFamily}
                    style={{
                      transform: hoveredOrActive ? 'scale(1.25)' : undefined,
                      transformOrigin: `center ${MARKER_HEIGHT - MARKER_ELLIPSE_RADIUS / 2}px`,
                      transition: 'transform .2s',
                    }}
                  >
                    {overlapCount}
                  </text>
                )}
              </svg>
            );
          }
        }
      })()}
    </MarkerTooltip>
  );
};

export default Marker;
